// Highwall Lift product architecture (final, approved).
// TWO physical products: The Buddy™ (Model A) and The Hank™ (Model B). Selling unit: set.
// Scissor vs boom lift is NOT a product variant. Lift type is informational only.

export const compatibilityNote =
  "Use is subject to the applicable Highwall product, lift compatibility, rated capacities, lift manufacturer requirements, Highwall instructions, and jobsite requirements.";

export const liftCompatibilityStatement =
  "Works with scissor lifts and boom lifts, subject to the applicable lift compatibility, rated capacities, and Highwall instructions.";

const IMG = {
  buddyMounted: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/hnmxvm2w_Highwall%20Scissor%20Lift%20Hanger%20on%20Skyjack3.jpg",
  buddySet: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/2hzy6y9m_Highwall%20Scissor%20Lift%20Hanger%20Pair.jpg",
  hankMounted: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/ujlxoadu_Boom%20Lift%20Hang.jpg",
  hankDetail1: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/je2x8ku1_hanger3.jpg",
  hankDetail2: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/htgmy3k7_Hanger4.jpg",
};

export const products = [
  {
    id: "buddy",
    sku: "HWL-BUDDY",
    slug: "buddy",
    to: "/products/buddy",
    legacyPath: "/products/scissor-lift-hanger",
    name: "The Buddy™",
    shortName: "Buddy™",
    fullName: "Highwall Lift Buddy™",
    model: "Model A",
    descriptor: "Interior Commercial Construction Material Hanger",
    tagline: "Built for work inside the four walls.",
    image: IMG.buddyMounted,
    galleryImages: [IMG.buddyMounted, IMG.buddySet],
    imageAlt: "Highwall Lift Buddy (Model A) interior construction material hanger mounted on a scissor lift during drywall installation",
    blurb: "The go-to choice for drywall and sheetrock installation. Mounts externally to the lift platform, keeping your workspace clear and safe. Safely supports single panels up to 106 lbs.",
    positioning: "The Buddy™ is the Highwall Lift solution for commercial interiors. By supporting drywall, sheetrock, and interior panels outside the lift platform, crews keep the deck clear for people and tools while positioning materials at height.",
    applications: ["Drywall / Sheetrock Installation", "Interior Panel Installation", "Large-Format Panel Positioning"],
    features: ["Clears deck space for workers", "No through-bolting required", "Fits Skyjack, JLG, Genie"],
    description: `The Highwall Lift Buddy™ (Model A) is designed for crews who need to keep their lift deck clear while positioning large panels at height.

By mounting externally to the lift cage, the hanger supports panels up to 106 lbs outside the working area, giving crews the space they need to work safely and efficiently.

**No through-bolting required** — the hanger attaches securely without permanent modifications to your lift, making it easy to install and remove as needed.

**Compatible with major scissor lift brands** including Skyjack, JLG, and Genie, so it integrates seamlessly with your existing fleet.`,
    specs: [
      { label: "Model", value: "Model A" },
      { label: "Capacity", value: "106 lbs (single panel)" },
      { label: "Mounting", value: "External, no through-bolting" },
      { label: "Compatibility", value: "Skyjack, JLG, Genie scissor lifts" },
      { label: "Construction", value: "Heavy-duty steel" },
      { label: "Sold As", value: "Set" },
    ],
    price: "$299",
    priceUnit: "/set",
    seo: {
      title: "Highwall Lift Buddy™ (Model A) | Interior Commercial Construction Material Hanger",
      description: "The Highwall Lift Buddy™ (Model A) is the interior commercial construction material hanger for drywall, sheetrock and interior panel installation. External mount, no through-bolting, fits Skyjack, JLG, Genie. $299/set.",
      keywords: "highwall lift buddy, drywall hanger, sheetrock hanger, scissor lift hanger, drywall installation equipment, interior panel installation, scissor lift material handling, boom lift material handling, commercial interior construction, aerial lift accessories",
    },
  },
  {
    id: "hank",
    sku: "HWL-HANK",
    slug: "hank",
    to: "/products/hank",
    legacyPath: "/products/boom-lift-hanger",
    name: "The Hank™",
    shortName: "Hank™",
    fullName: "Highwall Lift Hank™",
    model: "Model B",
    descriptor: "IMP, Glass & Building Envelope Material Hanger",
    tagline: "Built for work outside the four walls.",
    image: IMG.hankMounted,
    galleryImages: [IMG.hankMounted, IMG.hankDetail1, IMG.hankDetail2],
    imageAlt: "Highwall Lift Hank (Model B) building envelope material hanger mounted on a boom lift during IMP and architectural glass installation",
    blurb: "Designed for heavy-duty exterior panel installation including IMPs, architectural glass, and curtain wall glazing. UHMW pads protect panel surfaces. Supports single panels up to 106 lbs.",
    positioning: "The Hank™ is the Highwall Lift solution for the building envelope. By supporting insulated metal panels, architectural glass, curtain wall glazing, and exterior panels outside the lift platform, crews keep the deck clear while positioning large-format materials at height.",
    applications: ["Insulated Metal Panel (IMP) Installation", "Architectural Glass Panels", "Curtain Wall Glazing", "Exterior Panels / Building Envelope"],
    features: ["UHMW pads protect panel surfaces", "Designed to meet ANSI A92 Group B requirements", "Galvanized steel construction"],
    windNote: "Optimized for winds under 10mph",
    description: `The Highwall Lift Hank™ (Model B) is built for heavy-duty panel installation applications, including insulated metal panels (IMPs), architectural glass, and curtain wall glazing.

**UHMW pads** protect delicate panel surfaces during handling — critical for IMP and glass applications where surface damage is costly and visible.

**Designed to meet ANSI A92 Group B requirements** for aerial work platform accessories used in commercial construction applications.

**Galvanized steel construction** stands up to demanding jobsite conditions, providing reliable performance across multiple projects.

The hanger positions panels outside the platform, keeping the deck clear for crew movement and tool access.`,
    specs: [
      { label: "Model", value: "Model B" },
      { label: "Capacity", value: "106 lbs (single panel)" },
      { label: "Panel Protection", value: "UHMW pads" },
      { label: "Standard", value: "Designed to meet ANSI A92 Group B requirements" },
      { label: "Construction", value: "Galvanized steel" },
      { label: "Wind Rating", value: "Optimized for <10mph" },
      { label: "Sold As", value: "Set" },
    ],
    price: "$397",
    priceUnit: "/set",
    seo: {
      title: "Highwall Lift Hank™ (Model B) | IMP, Glass & Building Envelope Material Hanger",
      description: "The Highwall Lift Hank™ (Model B) is the IMP, glass and building envelope material hanger for insulated metal panel, architectural glass and curtain wall installation. UHMW pads, galvanized steel, designed to meet ANSI A92 Group B requirements. $397/set.",
      keywords: "highwall lift hank, boom lift hanger, IMP installation, insulated metal panel installation, architectural glass, glass installation, curtain wall, building envelope, exterior panel installation, boom lift material handling, aerial lift accessories",
    },
  },
];

export const applications = products;
export const getProductBySlug = (slug) => products.find((p) => p.slug === slug);
export const getProductByLegacyPath = (path) => products.find((p) => p.legacyPath === path);
